import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { Metamodel } from 'app/model/base/model';
import { Duration } from 'luxon';
import { CopySourceAdapter, commonCopySourceAdapts } from './copysource.adapter';
import { CopyActivity } from 'app/model/pipeline/copy.activity';
import { AzureHorizonDBCopySource } from 'app/model/pipeline/copysource/azure-horizon-db.copysource';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import { partitionOptionConst } from 'app/registries/consts/metadata-consts';
import { isLogicAppExpression } from 'app/shared/utils/expression.util';

// Azure HorizonDB is PostgreSQL wire-compatible, so it reuses the exact same copy-source
// serialization/behavior as Azure Database for PostgreSQL; only the emitted copy-source type
// differs ('AzureHorizonDBSource').
@AdapterRegistry.adapter({
    model: Metamodel.modelFor(AzureHorizonDBCopySource),
    asynchronous: true,
    adapts: {
        ...commonCopySourceAdapts,
        query: '*',
        queryTimeout: '*',
        partitionOption: '*',
        partitionSettings: {
            partitionColumnName: '*',
            partitionUpperBound: '*',
            partitionLowerBound: '*',
            partitionNames: '*'
        }
    }
})
export class AzureHorizonDBCopySourceAdapter extends CopySourceAdapter<
    AzureHorizonDBCopySource,
    Model.AzurePostgreSqlSource
> {
    protected override async _toResource(copySource: AzureHorizonDBCopySource): Promise<Model.AzurePostgreSqlSource> {
        const result: Model.AzurePostgreSqlSource = this._toTabularResource(
            copySource,
            Metamodel.modelFor(AzureHorizonDBCopySource).name
        );
        result.partitionOption = ValueAdapter.toResource(copySource, AzureHorizonDBCopySource.PARTITION_OPTION);
        if (AzureHorizonDBCopySource.PARTITION_OPTION.templateValue(copySource) != null) {
            result.partitionSettings = {
                partitionColumnName: ValueAdapter.toResource(
                    copySource,
                    AzureHorizonDBCopySource.PARTITION_COLUMN_NAME
                ),
                partitionUpperBound: ValueAdapter.toResource(
                    copySource,
                    AzureHorizonDBCopySource.PARTITION_UPPER_BOUND
                ),
                partitionLowerBound: ValueAdapter.toResource(
                    copySource,
                    AzureHorizonDBCopySource.PARTITION_LOWER_BOUND
                ),
                partitionNames: ValueAdapter.toResource(copySource, AzureHorizonDBCopySource.PARTITION_NAMES)
            };
        } else if (
            !stringIsNullOrEmpty(copySource.partitionOption) &&
            copySource.partitionOption !== partitionOptionConst.none
        ) {
            if (copySource.partitionOption === partitionOptionConst.dyamicRange) {
                result.partitionSettings = {
                    partitionColumnName: ValueAdapter.toResource(
                        copySource,
                        AzureHorizonDBCopySource.PARTITION_COLUMN_NAME
                    ),
                    partitionUpperBound: ValueAdapter.toResource(
                        copySource,
                        AzureHorizonDBCopySource.PARTITION_UPPER_BOUND
                    ),
                    partitionLowerBound: ValueAdapter.toResource(
                        copySource,
                        AzureHorizonDBCopySource.PARTITION_LOWER_BOUND
                    )
                };
            } else if (copySource.partitionOption === partitionOptionConst.physicalPartitionsOfTable) {
                result.partitionSettings = {
                    partitionNames: ValueAdapter.toResource(copySource, AzureHorizonDBCopySource.PARTITION_NAMES)
                };
            }
        }

        try {
            result.queryTimeout =
                typeof copySource.queryTimeout === 'number'
                    ? Duration.fromObject({ minutes: copySource.queryTimeout }).toFormat('hh:mm:ss')
                    : ValueAdapter.toResource(copySource, AzureHorizonDBCopySource.QUERY_TIMEOUT);
        } catch {
            this._logError('Failed to convert AzureHorizonDBCopySource queryTimeout into TimeSpan');
        }
        return result;
    }

    protected override async _toModel(
        resource: Model.AzurePostgreSqlSource,
        copyActivity: CopyActivity
    ): Promise<AzureHorizonDBCopySource> {
        const result: AzureHorizonDBCopySource = this._toTabularModel(
            resource,
            copyActivity,
            AzureHorizonDBCopySource.MODEL
        );
        if (resource.partitionOption !== undefined) {
            ValueAdapter.toModel(result, AzureHorizonDBCopySource.PARTITION_OPTION, resource.partitionOption);
        } else {
            delete result.partitionOption;
        }
        if (resource.partitionSettings != null) {
            ValueAdapter.toModel(
                result,
                AzureHorizonDBCopySource.PARTITION_NAMES,
                resource.partitionSettings.partitionNames
            );
            ValueAdapter.toModel(
                result,
                AzureHorizonDBCopySource.PARTITION_COLUMN_NAME,
                resource.partitionSettings.partitionColumnName
            );
            ValueAdapter.toModel(
                result,
                AzureHorizonDBCopySource.PARTITION_LOWER_BOUND,
                resource.partitionSettings.partitionLowerBound
            );
            ValueAdapter.toModel(
                result,
                AzureHorizonDBCopySource.PARTITION_UPPER_BOUND,
                resource.partitionSettings.partitionUpperBound
            );
        }

        try {
            if (resource.queryTimeout == null) {
                result.queryTimeout = undefined;
            } else {
                if (typeof resource.queryTimeout === 'string' && !isLogicAppExpression(resource.queryTimeout)) {
                    const parts = resource.queryTimeout.split(':');
                    if (parts.length === 3) {
                        const duration = Duration.fromObject({
                            hours: parseInt(parts[0], 10),
                            minutes: parseInt(parts[1], 10),
                            seconds: parseInt(parts[2], 10)
                        });
                        result.queryTimeout = duration.as('minutes');
                    } else {
                        this._logError('Failed to convert AzureHorizonDBCopySource queryTimeout into number');
                    }
                } else {
                    ValueAdapter.toModel(result, AzureHorizonDBCopySource.QUERY_TIMEOUT, resource.queryTimeout);
                }
            }
        } catch {
            this._logError('Failed to convert AzureHorizonDBCopySource queryTimeout into number');
        }
        return result;
    }
}
